import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Activity,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Zap,
  Calendar,
  Monitor,
  Smartphone,
} from "lucide-react";

const TIME_PRESETS = [
  { label: "24h",    value: "24h",  ms: 24 * 60 * 60 * 1000 },
  { label: "7d",     value: "7d",   ms: 7  * 24 * 60 * 60 * 1000 },
  { label: "30d",    value: "30d",  ms: 30 * 24 * 60 * 60 * 1000 },
  { label: "90d",    value: "90d",  ms: 90 * 24 * 60 * 60 * 1000 },
  { label: "All",    value: "all",  ms: null },
];

function getPresetDates(preset) {
  if (!preset || preset.ms === null) return { from: null, to: null };
  const to = new Date();
  const from = new Date(Date.now() - preset.ms);
  return { from: from.toISOString(), to: to.toISOString() };
}

const CHART_COLORS = [
  "#2563eb", "#7c3aed", "#0891b2", "#059669",
  "#d97706", "#dc2626", "#0284c7", "#9333ea",
  "#0d9488", "#b45309", "#16a34a", "#e11d48",
  "#6366f1", "#f97316", "#14b8a6",
];

const METHOD_COLORS = {
  GET: "#2563eb",
  POST: "#059669",
  PUT: "#d97706",
  PATCH: "#7c3aed",
  DELETE: "#dc2626",
};

// Strip /api/v1/ prefix and make URL human-readable for chart labels
function shortLabel(url) {
  if (!url) return "Unknown";
  const clean = url.replace(/^\/api\/v1\//, "").replace(/^\//, "");
  const parts = clean.split("/");
  return parts
    .slice(0, 2)
    .map((p) => p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(" / ");
}

function StatCard({ icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-2.5 rounded-lg shrink-0 ${colors[color]}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-800 mt-0.5 truncate">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4 animate-pulse">
      <div className="w-10 h-10 bg-gray-100 rounded-lg shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-3 bg-gray-100 rounded w-24" />
        <div className="h-7 bg-gray-200 rounded w-16" />
      </div>
    </div>
  );
}

const EndpointTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm max-w-xs">
      <p className="font-mono text-xs text-gray-500 mb-1 break-all">{payload[0]?.payload?.fullUrl}</p>
      <p className="font-semibold text-gray-800">{payload[0]?.value?.toLocaleString()} hits</p>
    </div>
  );
};

const MethodTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-semibold text-gray-700">{label}</p>
      <p className="text-xs mt-0.5" style={{ color: payload[0]?.fill }}>
        {payload[0]?.value?.toLocaleString()} requests
      </p>
    </div>
  );
};

export default function ApiHitsAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [activePreset, setActivePreset] = useState("7d");
  const [platform, setPlatform] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchStats = useCallback(async ({ preset, plt, cFrom, cTo } = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (plt && plt !== "all") params.platform = plt;

      if (preset === "custom") {
        if (cFrom) params.from = new Date(cFrom).toISOString();
        if (cTo) {
          const d = new Date(cTo);
          d.setHours(23, 59, 59, 999);
          params.to = d.toISOString();
        }
      } else {
        const presetObj = TIME_PRESETS.find((p) => p.value === preset);
        const { from, to } = getPresetDates(presetObj);
        if (from) params.from = from;
        if (to) params.to = to;
      }

      const res = await axiosInstance.get(API_ENDPOINTS.logsStats, { params });
      setStats(res.data?.stats ?? null);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load API stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats({ preset: activePreset, plt: platform, cFrom: customFrom, cTo: customTo });
  }, [fetchStats, activePreset, platform]);

  const handlePreset = (val) => setActivePreset(val);

  const handleCustomApply = () => {
    if (!customFrom && !customTo) return;
    setActivePreset("custom");
    fetchStats({ preset: "custom", plt: platform, cFrom: customFrom, cTo: customTo });
  };

  // --- Derived data ---

  const total = stats?.totalRequests ?? 0;
  const successful = stats?.successfulRequests ?? 0;
  const failed = stats?.failedRequests ?? 0;
  const last24h = stats?.last24Hours?.requests ?? 0;
  const successRate = total > 0 ? ((successful / total) * 100).toFixed(1) : "0.0";

  // Top endpoints chart — horizontal bar, sorted desc
  const endpointChartData = Object.entries(stats?.topEndpoints ?? {})
    .map(([url, count]) => ({ fullUrl: url, name: shortLabel(url), hits: count }))
    .sort((a, b) => b.hits - a.hits)
    .slice(0, 15);

  // HTTP Methods chart
  const methodChartData = Object.entries(stats?.methods ?? {})
    .map(([method, count]) => ({ method, count }))
    .sort((a, b) => b.count - a.count);

  // Status codes — group into 2xx, 3xx, 4xx, 5xx
  const statusGroups = { "2xx": 0, "3xx": 0, "4xx": 0, "5xx": 0 };
  Object.entries(stats?.statusCodes ?? {}).forEach(([code, count]) => {
    const n = parseInt(code);
    if (n >= 200 && n < 300) statusGroups["2xx"] += count;
    else if (n >= 300 && n < 400) statusGroups["3xx"] += count;
    else if (n >= 400 && n < 500) statusGroups["4xx"] += count;
    else if (n >= 500) statusGroups["5xx"] += count;
  });
  const statusChartData = Object.entries(statusGroups)
    .map(([group, count]) => ({ group, count }))
    .filter((d) => d.count > 0);

  const statusColors = { "2xx": "#059669", "3xx": "#0891b2", "4xx": "#d97706", "5xx": "#dc2626" };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Hit Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Total requests, top endpoints, methods and status codes across all APIs
          </p>
        </div>
        <button
          onClick={() => fetchStats({ preset: activePreset, plt: platform, cFrom: customFrom, cTo: customTo })}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50 self-start transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex flex-wrap items-end gap-5">
        {/* Time Presets */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Time Range
          </label>
          <div className="flex gap-1.5">
            {TIME_PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => handlePreset(p.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                  activePreset === p.value
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date Range */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Custom Range</label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400"
            />
            <span className="text-xs text-gray-400">to</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-blue-400"
            />
            <button
              onClick={handleCustomApply}
              disabled={!customFrom && !customTo}
              className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Platform Filter */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-500">Platform</label>
          <div className="flex gap-1.5">
            {[
              { value: "all", label: "All", icon: null },
              { value: "web", label: "Web", icon: <Monitor className="w-3 h-3" /> },
              { value: "app", label: "App", icon: <Smartphone className="w-3 h-3" /> },
            ].map((p) => (
              <button
                key={p.value}
                onClick={() => setPlatform(p.value)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
                  platform === p.value
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {p.icon}
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 text-sm rounded">
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <StatCard
              icon={<Activity className="w-5 h-5" />}
              label="Total Requests"
              value={total.toLocaleString()}
              sub="All time"
              color="blue"
            />
            <StatCard
              icon={<CheckCircle className="w-5 h-5" />}
              label="Success Rate"
              value={`${successRate}%`}
              sub={`${successful.toLocaleString()} successful`}
              color="green"
            />
            <StatCard
              icon={<AlertCircle className="w-5 h-5" />}
              label="Failed Requests"
              value={failed.toLocaleString()}
              sub="Status 4xx / 5xx"
              color="red"
            />
            <StatCard
              icon={<Zap className="w-5 h-5" />}
              label="Last 24 Hours"
              value={last24h.toLocaleString()}
              sub={`${stats?.last24Hours?.failures ?? 0} failures`}
              color="amber"
            />
          </>
        )}
      </div>

      {/* Top Endpoints Chart */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gray-400" />
          <div>
            <h2 className="text-base font-semibold text-gray-800">Top API Endpoints — Hit Count</h2>
            <p className="text-xs text-gray-400 mt-0.5">Top 15 most called endpoints (hover to see full URL)</p>
          </div>
        </div>
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : !endpointChartData.length ? (
          <div className="h-96 flex items-center justify-center text-sm text-gray-400">
            No endpoint data available
          </div>
        ) : (
          <div className="px-4 pb-4 pt-2">
            <ResponsiveContainer width="100%" height={endpointChartData.length * 42 + 40}>
              <BarChart
                data={endpointChartData}
                layout="vertical"
                margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  tick={{ fontSize: 11, fill: "#374151" }}
                />
                <Tooltip content={<EndpointTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey="hits" name="Hits" radius={[0, 6, 6, 0]} label={{ position: "right", fontSize: 11, fill: "#6b7280", formatter: (v) => v.toLocaleString() }}>
                  {endpointChartData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Methods + Status Codes side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HTTP Methods */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Requests by HTTP Method</h2>
            <p className="text-xs text-gray-400 mt-0.5">GET, POST, PUT, PATCH, DELETE breakdown</p>
          </div>
          {loading ? (
            <div className="h-52 flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : !methodChartData.length ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">
              No data available
            </div>
          ) : (
            <div className="px-4 pb-4 pt-2">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={methodChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="method" tick={{ fontSize: 12, fill: "#374151", fontWeight: 600 }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip content={<MethodTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                  <Bar dataKey="count" name="Requests" radius={[4, 4, 0, 0]}>
                    {methodChartData.map((entry) => (
                      <Cell
                        key={entry.method}
                        fill={METHOD_COLORS[entry.method] ?? "#6366f1"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Status Codes */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Responses by Status Group</h2>
            <p className="text-xs text-gray-400 mt-0.5">2xx success, 3xx redirect, 4xx client error, 5xx server error</p>
          </div>
          {loading ? (
            <div className="h-52 flex items-center justify-center">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : !statusChartData.length ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">
              No data available
            </div>
          ) : (
            <div className="px-4 pb-4 pt-2">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statusChartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="group" tick={{ fontSize: 13, fill: "#374151", fontWeight: 600 }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                  <Tooltip
                    formatter={(v) => [v.toLocaleString(), "Requests"]}
                    contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                    cursor={{ fill: "rgba(0,0,0,0.03)" }}
                  />
                  <Bar dataKey="count" name="Requests" radius={[4, 4, 0, 0]}>
                    {statusChartData.map((entry) => (
                      <Cell key={entry.group} fill={statusColors[entry.group] ?? "#6366f1"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Endpoints Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Endpoint Breakdown</h2>
          <p className="text-xs text-gray-400 mt-0.5">Full URL paths with hit count and share</p>
        </div>
        {loading ? (
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
                <div className="w-5 h-3 bg-gray-100 rounded" />
                <div className="flex-1 h-3.5 bg-gray-200 rounded w-48" />
                <div className="h-3 bg-gray-100 rounded w-14" />
                <div className="w-28 h-2 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : !endpointChartData.length ? (
          <div className="px-6 py-12 text-center text-sm text-gray-400">No endpoint data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-6 py-3 text-left font-medium w-10">#</th>
                  <th className="px-6 py-3 text-left font-medium">Endpoint</th>
                  <th className="px-6 py-3 text-right font-medium">Hits</th>
                  <th className="px-6 py-3 text-left font-medium w-40">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {endpointChartData.map((row, i) => {
                  const maxHits = endpointChartData[0]?.hits || 1;
                  const pct = Math.round((row.hits / maxHits) * 100);
                  const totalPct = total > 0 ? ((row.hits / total) * 100).toFixed(1) : "0.0";
                  return (
                    <tr key={row.fullUrl} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-xs text-gray-400">{i + 1}</td>
                      <td className="px-6 py-3">
                        <p className="font-medium text-gray-800">{row.name}</p>
                        <p className="text-xs text-gray-400 font-mono mt-0.5">{row.fullUrl}</p>
                      </td>
                      <td className="px-6 py-3 text-right">
                        <span className="font-semibold text-gray-700">{row.hits.toLocaleString()}</span>
                        <span className="text-xs text-gray-400 ml-1.5">({totalPct}%)</span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
